// Studio OS — QR labels
//
// Gear: an item can be linked to one QR code. Studios can print batches of
// blank codes from here (onto labels, tags, cards — whatever they use), and
// they can also link QR codes they already have from elsewhere. A code
// belongs to nothing until it's scanned and linked (Scan page). Replacing an
// item's code makes the old one stop working. Stored on the item: item.code.
// Codes printed here are 6 characters from an alphabet without look-alikes
// (no 0/O, 1/I/L), e.g. "7K2M9Q"; a code from elsewhere is kept as scanned.
//
// Locations: printed once with their name, and the label follows the
// location through renames: location_codes setting { "Store #1": "Q8V3XA" }.
//
// What a QR contains (all upper case: QR stores A–Z 0–9 and a few symbols
// more compactly, which keeps codes readable on small labels):
//   label web address set   → HTTPS://<ADDRESS>/I/7K2M9Q   (any phone camera opens it)
//   not set yet             → STUDIOS:I:7K2M9Q              (in-app scanner only)
// Locations use /L/ and STUDIOS:L:. The scanner understands both forever, so
// codes printed before the address is chosen keep working in the app.

const LABEL_ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";
const QRGEN_SRC = "https://cdnjs.cloudflare.com/ajax/libs/qrcode-generator/1.4.4/qrcode.min.js";
const QRGEN_SRI = "sha384-mZT2gIty7ZDdOGkxfP6joZcYdMW1Jvj9dRlfpTmaJAKKXTqzygtB22k7FLe+KZC1";

function newLabelCode(taken) {
  for (;;) {
    const bytes = crypto.getRandomValues(new Uint8Array(6));
    const code = [...bytes].map(b => LABEL_ALPHABET[b % LABEL_ALPHABET.length]).join("");
    if (!taken.has(code)) return code;
  }
}

function takenLabelCodes() {
  const taken = new Set(Object.values(load(STORE_ORG, "location_codes", {}) || {}));
  for (const i of window.INVENTORY || []) if (i.code) taken.add(i.code);
  return taken;
}

// Fresh codes for a batch of blank QR codes.
function newQRCodes(count) {
  const taken = takenLabelCodes();
  const codes = [];
  while (codes.length < count) {
    const code = newLabelCode(taken);
    taken.add(code);
    codes.push(code);
  }
  return codes;
}

// Case doesn't matter: QR text from elsewhere may come in either.
function itemByCode(code) {
  const want = String(code || "").toUpperCase();
  return want ? (window.INVENTORY || []).find(i => i.code && String(i.code).toUpperCase() === want) || null : null;
}

function saveQRChange() {
  save(STORE_ORG, "inventory", INVENTORY);
  window.dispatchEvent(new CustomEvent("studiOS:inventory-updated"));
}

// Links a QR code to an item. The item's old code (if any) stops working;
// a code that was on another item moves here.
function linkQRCode(item, code) {
  const prev = itemByCode(code);
  if (prev && prev !== item) delete prev.code;
  item.code = code;
  saveQRChange();
}

function unlinkQRCode(item) {
  delete item.code;
  saveQRChange();
}

// Long QR text from elsewhere (often a URL), shortened for display: the
// start and the end, which is usually where the identifying part is.
function shortQRCode(code) {
  const s = String(code || "").replace(/^https?:\/\//i, "");
  return s.length > 18 ? `${s.slice(0, 10)}…${s.slice(-7)}` : s;
}

function locationCodes() {
  return load(STORE_ORG, "location_codes", {}) || {};
}

function ensureLocationCodes(names) {
  const map = { ...locationCodes() };
  const taken = takenLabelCodes();
  let added = 0;
  for (const name of names) {
    if (map[name]) continue;
    map[name] = newLabelCode(taken);
    taken.add(map[name]);
    added++;
  }
  if (added) save(STORE_ORG, "location_codes", map);
  return map;
}

// Called by renameInventoryLocation: the label follows the location.
function renameLocationCode(from, to) {
  const map = locationCodes();
  if (!map[from] || map[to]) return;
  map[to] = map[from];
  delete map[from];
  save(STORE_ORG, "location_codes", map);
}

function labelBaseAddress() {
  const raw = (load(STORE_ORG, "label_base_url", "") || "").trim();
  return raw.replace(/^https?:\/\//i, "").replace(/\/+$/, "");
}

// kind: "item" | "location"
function labelPayload(kind, code) {
  const k = kind === "item" ? "I" : "L";
  const base = labelBaseAddress();
  return (base ? `HTTPS://${base}/${k}/${code}` : `STUDIOS:${k}:${code}`).toUpperCase();
}

// Reads any label payload back: { kind, code } or null.
function parseLabelPayload(text) {
  const t = (text || "").trim();
  let m = t.match(/^STUDIOS:(I|L):([A-Z0-9]+)$/i);
  if (m) return { kind: m[1].toUpperCase() === "I" ? "item" : "location", code: m[2].toUpperCase() };
  m = t.match(/^https?:\/\/[^/]+\/(I|L)\/([A-Z0-9]+)\/?$/i);
  if (m) return { kind: m[1].toUpperCase() === "I" ? "item" : "location", code: m[2].toUpperCase() };
  return null;
}

// ── QR rendering ──────────────────────────────────────────────────────
let qrGenLoading = null;
function loadQRGen() {
  if (window.qrcode) return Promise.resolve(window.qrcode);
  if (!qrGenLoading) {
    qrGenLoading = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = QRGEN_SRC; s.integrity = QRGEN_SRI; s.crossOrigin = "anonymous";
      s.onload = () => resolve(window.qrcode);
      s.onerror = () => { qrGenLoading = null; reject(new Error("Couldn't load the QR generator.")); };
      document.head.appendChild(s);
    });
  }
  return qrGenLoading;
}

// An SVG string, sized by its container. Two modules of quiet zone; the
// label's own white margin adds the rest.
function qrSvg(text) {
  const q = window.qrcode(0, "M");
  q.addData(text, /^[0-9A-Z $%*+\-./:]*$/.test(text) ? "Alphanumeric" : "Byte");
  q.make();
  const n = q.getModuleCount(), quiet = 2, size = n + quiet * 2;
  let d = "";
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
    if (q.isDark(r, c)) d += `M${c + quiet} ${r + quiet}h1v1h-1z`;
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges">`
    + `<rect width="${size}" height="${size}" fill="#fff"/><path d="${d}" fill="#000"/></svg>`;
}

// ── Label styles ──────────────────────────────────────────────────────
const LABEL_STYLES = {
  code:     { label: "QR + code",  sub: "Code printed beside the QR, for typing if it's damaged", for: "blank", sheet: "a4-65" },
  qr:       { label: "QR only",    sub: "Just the QR, as small as it gets",                      for: "blank", sheet: "a4-65" },
  location: { label: "Location",   sub: "QR, name, studio",                                      for: "location", sheet: "a4-8" },
};

const escHtml = s => String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// One label's inner HTML, laid out in mm for a w × h box.
// data: { payload, code, name, studio }
function labelHtml(style, data, w, h) {
  const pad = Math.min(2, h * 0.08);
  const qrBox = (size) => `<div style="flex:none;width:${size}mm;height:${size}mm">${qrSvg(data.payload)}</div>`;
  if (style === "qr") {
    const size = Math.min(w, h) - pad * 2;
    return `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center">${qrBox(size)}</div>`;
  }
  if (style === "location") {
    const size = Math.min(h - pad * 2, w * 0.45);
    return `<div style="width:100%;height:100%;display:flex;align-items:center;gap:${pad * 1.5}mm;padding:0 ${pad * 1.5}mm 0 ${pad}mm;box-sizing:border-box">`
      + qrBox(size)
      + `<div style="min-width:0;display:flex;flex-direction:column;gap:${h * 0.03}mm">`
      + `<div style="font-size:${Math.max(1.8, h * 0.045)}mm;letter-spacing:.12em;color:#555">LOCATION</div>`
      + `<div style="font-size:${Math.min(h * 0.16, 9)}mm;font-weight:600;line-height:1.05;overflow-wrap:anywhere">${escHtml(data.name)}</div>`
      + `<div style="font-size:${Math.max(2, h * 0.05)}mm;color:#444;line-height:1.3">Scan to see what's here</div>`
      + (data.studio ? `<div style="font-size:${Math.max(1.8, h * 0.042)}mm;color:#555;margin-top:${h * 0.03}mm">${escHtml(data.studio)}</div>` : "")
      + `</div></div>`;
  }
  // Blank gear code: QR, then the code (and studio, when there's room).
  const size = Math.min(h - pad * 2, w * 0.55);
  const codeSize = Math.min(h * 0.2, (w - size - pad * 3) / 4.3);
  return `<div style="width:100%;height:100%;display:flex;align-items:center;gap:${pad}mm;padding:0 ${pad}mm;box-sizing:border-box">`
    + qrBox(size)
    + `<div style="min-width:0;display:flex;flex-direction:column;gap:${h * 0.04}mm">`
    + `<div style="font-family:ui-monospace,Menlo,monospace;font-weight:600;font-size:${codeSize}mm;letter-spacing:.04em;white-space:nowrap">${escHtml(data.code)}</div>`
    + (data.studio && h >= 18 ? `<div style="font-size:${Math.max(1.8, h * 0.1)}mm;color:#555;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escHtml(data.studio)}</div>` : "")
    + `</div></div>`;
}

// ── Label sheets (published Avery dimensions, mm) ─ ───────────────────
// page: [w, h]; label: [w, h]; pitch: [x, y] centre-to-centre; margin: [left, top]
const LABEL_SHEETS = {
  "a4-24":  { name: "A4 · 24 per sheet",  sku: "Avery L7159", page: [210, 297], cols: 3, rows: 8,  label: [63.5, 33.9], pitch: [66.04, 33.9], margin: [7.21, 12.9] },
  "a4-65":  { name: "A4 · 65 per sheet",  sku: "Avery L7651", page: [210, 297], cols: 5, rows: 13, label: [38.1, 21.2], pitch: [40.64, 21.2], margin: [4.67, 10.7] },
  "a4-8":   { name: "A4 · 8 per sheet",   sku: "Avery L7165", page: [210, 297], cols: 2, rows: 4,  label: [99.1, 67.7], pitch: [101.6, 67.7], margin: [4.65, 13.1] },
  "us-30":  { name: "Letter · 30 per sheet", sku: "Avery 5160", page: [215.9, 279.4], cols: 3, rows: 10, label: [66.68, 25.4], pitch: [69.85, 25.4], margin: [4.76, 12.7] },
  "us-80":  { name: "Letter · 80 per sheet", sku: "Avery 5167", page: [215.9, 279.4], cols: 4, rows: 20, label: [44.45, 12.7], pitch: [52.32, 12.7], margin: [7.62, 12.7] },
  "us-6":   { name: "Letter · 6 per sheet",  sku: "Avery 5164", page: [215.9, 279.4], cols: 2, rows: 3,  label: [101.6, 84.67], pitch: [106.43, 84.67], margin: [3.97, 12.7] },
};

const LETTER_TWIN = { "a4-24": "us-30", "a4-65": "us-80", "a4-8": "us-6" };

// Pages of label HTML. start: 1-based position of the first free label.
function sheetPages(sheet, style, entries, start) {
  const per = sheet.cols * sheet.rows;
  const slots = [...Array(Math.max(0, start - 1)).fill(null), ...entries];
  const pages = [];
  for (let p = 0; p < slots.length; p += per) {
    let html = "";
    slots.slice(p, p + per).forEach((entry, i) => {
      if (!entry) return;
      const col = i % sheet.cols, row = Math.floor(i / sheet.cols);
      const x = sheet.margin[0] + col * sheet.pitch[0], y = sheet.margin[1] + row * sheet.pitch[1];
      html += `<div class="lbl" style="left:${x}mm;top:${y}mm;width:${sheet.label[0]}mm;height:${sheet.label[1]}mm">`
        + labelHtml(style, entry, sheet.label[0], sheet.label[1]) + `</div>`;
    });
    pages.push(html);
  }
  return pages;
}

const LABEL_PAGE_CSS = (sheet) => `
  .sheet { position: relative; width: ${sheet.page[0]}mm; height: ${sheet.page[1]}mm; background: #fff; overflow: hidden; }
  .lbl { position: absolute; overflow: hidden; color: #000; background: #fff;
         font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif; }
  .lbl svg { width: 100%; height: 100%; display: block; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }`;

function printLabelPages(sheet, pages) {
  const frame = document.createElement("iframe");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>StudiOS labels</title><style>
    @page { size: ${sheet.page[0]}mm ${sheet.page[1]}mm; margin: 0; }
    html, body { margin: 0; padding: 0; }
    .sheet { page-break-after: always; break-after: page; }
    .sheet:last-child { page-break-after: auto; break-after: auto; }
    ${LABEL_PAGE_CSS(sheet)}
  </style></head><body>${pages.map(p => `<div class="sheet">${p}</div>`).join("")}</body></html>`);
  doc.close();
  frame.contentWindow.focus();
  // Give the SVGs a frame to lay out before the print dialog opens.
  setTimeout(() => {
    frame.contentWindow.print();
    setTimeout(() => frame.remove(), 1000);
  }, 100);
}

// ── Print screen ──────────────────────────────────────────────────────
// mode "blank": a batch of unlinked gear QR codes.
// mode "location": one label per location, with its name.
function LabelPrintModal({ mode = "blank", locations = [], onClose }) {
  const { studioName } = useStudio();
  const firstStyle = mode === "location" ? "location" : "code";
  const [style, setStyle] = React.useState(firstStyle);
  const [sheetId, setSheetId] = React.useState(LABEL_STYLES[firstStyle].sheet);
  const sheet = LABEL_SHEETS[sheetId];
  const per = sheet.cols * sheet.rows;
  const [howMany, setHowMany] = React.useState(per);
  const [start, setStart] = React.useState(1);
  const [ready, setReady] = React.useState(!!window.qrcode);
  const [error, setError] = React.useState("");
  const base = labelBaseAddress();

  React.useEffect(() => { loadQRGen().then(() => setReady(true), e => setError(e.message)); }, []);
  React.useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Keep the paper size when switching style: each A4 default has a Letter twin.
  const pickStyle = (id) => {
    setStyle(id);
    const a4 = LABEL_STYLES[id].sheet;
    setSheetId(sheetId.startsWith("us") ? LETTER_TWIN[a4] : a4);
  };
  // A new sheet size resets the count to one full sheet.
  const pickSheet = (id) => {
    setSheetId(id);
    const s = LABEL_SHEETS[id];
    setHowMany(s.cols * s.rows);
    setStart(1);
  };

  const count = mode === "location" ? locations.length : howMany;
  const pages = Math.ceil((count + start - 1) / per);

  // Location codes are created when printing; gear codes too. The preview
  // uses stand-in codes so opening this screen changes nothing.
  function entries() {
    if (mode === "location") {
      const map = ensureLocationCodes(locations);
      return locations.map(name => ({ payload: labelPayload("location", map[name]), name, studio: studioName }));
    }
    return newQRCodes(howMany).map(code => ({ payload: labelPayload("item", code), code, studio: studioName }));
  }
  const preview = React.useMemo(() => {
    if (!ready) return "";
    const n = Math.min(count, per - start + 1);
    const sample = mode === "location"
      ? locations.slice(0, n).map(name => ({ payload: labelPayload("location", locationCodes()[name] || "SAMPLE"), name, studio: studioName }))
      : Array.from({ length: n }, (_, i) => {
          const code = "SAMPL" + LABEL_ALPHABET[i % LABEL_ALPHABET.length];
          return { payload: labelPayload("item", code), code, studio: studioName };
        });
    return sheetPages(sheet, style, sample, start)[0] || "";
  }, [ready, sheetId, style, start, count, locations, studioName, base]);

  const scale = 260 / sheet.page[0]; // preview px per mm

  // Portalled: it opens from inside other dialogs (Inventory settings).
  return ReactDOM.createPortal(
    <div className="modal-overlay lp-overlay" onClick={onClose}>
      <div className="modal lp-modal" role="dialog" aria-modal="true" aria-label="Print labels" onClick={e => e.stopPropagation()}>
        <div className="lp-head">
          <div>
            <div className="lp-kicker mono">{mode === "location" ? "Locations · Labels" : "Gear · QR codes"}</div>
            <h2 className="lp-title">
              {mode === "location"
                ? `Print ${count} location label${count === 1 ? "" : "s"}`
                : "Print QR codes"}
            </h2>
          </div>
          <button type="button" className="drawer-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="lp-body">
          <div className="lp-form">
            {mode === "blank" && (
              <>
                <p className="lp-hint" style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: "var(--text-secondary)" }}>
                  Each code is unique and not linked to anything yet. Print them on labels, tags or cards and keep some handy: scan one on a phone to link it to a piece of gear.
                </p>
                <div className="lp-field">
                  <div className="lp-label">Style</div>
                  <div className="lp-styles">
                    {Object.entries(LABEL_STYLES).filter(([, s]) => s.for === "blank").map(([id, s]) => (
                      <button key={id} type="button" className={"lp-style" + (style === id ? " on" : "")} onClick={() => pickStyle(id)}>
                        <span className="lp-style-name">{s.label}</span>
                        <span className="lp-style-sub">{s.sub}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <label className="lp-field">
              <span className="lp-label">Sheet layout</span>
              <select className="st-select" value={sheetId} onChange={e => pickSheet(e.target.value)}>
                {Object.entries(LABEL_SHEETS).map(([id, s]) => (
                  <option key={id} value={id}>{s.name} — {s.sku} ({s.label[0]} × {s.label[1]} mm)</option>
                ))}
              </select>
            </label>

            {mode === "blank" && (
              <label className="lp-field">
                <span className="lp-label">How many</span>
                <span className="lp-start">
                  <input className="st-input" type="number" min={1} max={2000} value={howMany}
                    onChange={e => setHowMany(Math.max(1, Math.min(2000, Number(e.target.value) || 1)))} />
                  <span className="lp-hint">{per} fit on a sheet.</span>
                </span>
              </label>
            )}

            <label className="lp-field">
              <span className="lp-label">Start at label</span>
              <span className="lp-start">
                <input className="st-input" type="number" min={1} max={per} value={start}
                  onChange={e => setStart(Math.max(1, Math.min(per, Number(e.target.value) || 1)))} />
                <span className="lp-hint">Skip labels already used on a part-used sheet.</span>
              </span>
            </label>

            <div className={"lp-note" + (base ? "" : " warn")}>
              {base
                ? <>Codes open <span className="mono">https://{base}</span>, so any phone camera works.</>
                : <>No web address set, so these work with the StudiOS scanner only. Set one in Settings → Studio → Labels to let any phone camera open them. Anything printed now keeps working in the app.</>}
            </div>
            {error && <div className="lp-note warn" role="alert">{error}</div>}
          </div>

          <div className="lp-preview">
            <div className="lp-paper" style={{ width: sheet.page[0] * scale, height: sheet.page[1] * scale }}>
              <iframe title="Label preview" className="lp-frame"
                style={{ width: sheet.page[0] + "mm", height: sheet.page[1] + "mm", transform: `scale(${scale / 3.7795})` }}
                srcDoc={`<!doctype html><html><head><style>html,body{margin:0}${LABEL_PAGE_CSS(sheet)} .lbl{outline:0.2mm dashed #bbb}</style></head><body><div class="sheet">${preview}</div></body></html>`} />
            </div>
            <div className="lp-hint">{pages} sheet{pages === 1 ? "" : "s"} · first sheet shown · dashed lines don't print</div>
          </div>
        </div>

        <div className="lp-foot">
          <span className="lp-hint">In the print dialog, choose “Actual size” (100% scale).</span>
          <span style={{ flex: 1 }} />
          <button type="button" className="tb-btn ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="tb-btn" disabled={!ready || !count}
            onClick={() => printLabelPages(sheet, sheetPages(sheet, style, entries(), start))}>
            Print
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

Object.assign(window, {
  LabelPrintModal, ensureLocationCodes, renameLocationCode, locationCodes,
  newQRCodes, itemByCode, linkQRCode, unlinkQRCode, shortQRCode,
  labelPayload, parseLabelPayload, LABEL_STYLES, LABEL_SHEETS, loadQRGen, qrSvg, LABEL_ALPHABET,
});
